import DetailsButton from '@admin-components/DetailsButton.tsx';
import { DATAGRIDPRO_DEFAULTS, DATE_VALUE_GETTER } from '@admin-const';
import { useFetchUsersQuery } from '@admin-services';
import { DataGridPro, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid-pro';
import { RootState } from '@store';
import { GetUsersAdminResponse } from '@types';
import { useCallback, useEffect, useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AutoSizer from '@components/AutoSizer';

const UserTable = () => {
  const navigate = useNavigate();
  const { useUtcTime, use24HourFormat } = useSelector(
    (state: RootState) => ({
      useUtcTime: state.admin.useUtcTime,
      use24HourFormat: state.admin.use24HourFormat
    }),
    shallowEqual
  );
  const { data: users, isLoading, error } = useFetchUsersQuery();
  const onUserClicked = useCallback(
    (params: GridRenderCellParams<GetUsersAdminResponse> | GridRowParams<GetUsersAdminResponse>) => {
      const userEmail = params.row.user_email;
      const htmlEncodedEmail = encodeURIComponent(userEmail);
      const detailUrl = `/admin/users/${htmlEncodedEmail}`;
      navigate(detailUrl);
    },
    [navigate]
  );
  const columns = useMemo(() => {
    const columns: GridColDef[] = [
      { field: 'user_email', headerName: 'Email', width: 250 },
      { field: 'number_of_ip', headerName: '# IP Addresses', width: 200 },
      { field: 'number_of_groups', headerName: '# Groups', width: 200 },
      { field: 'number_of_projects', headerName: '# Projects', width: 200 },
      {
        field: 'last_active',
        headerName: 'Last Active',
        width: use24HourFormat ? 200 : 220,
        valueGetter: (value) => DATE_VALUE_GETTER(value, useUtcTime, use24HourFormat)
      },
      // add a column for a detail button,
      {
        field: 'details',
        headerName: '',
        flex: 1,
        sortable: false,
        renderCell: (params: GridRenderCellParams<GetUsersAdminResponse>) => {
          return <DetailsButton<GridRenderCellParams<GetUsersAdminResponse>> params={params} onClick={onUserClicked} />;
        }
      }
    ];
    return columns;
  }, [onUserClicked, use24HourFormat, useUtcTime]);

  useEffect(() => {
    if (error) {
      toast.error('Failed to load users for admin table');
    }
  }, [error]);

  return (
    <AutoSizer>
      {({ height, width }) => (
        <DataGridPro<GetUsersAdminResponse>
          // \n
          style={{ height, width }}
          rows={users || []}
          columns={columns}
          getRowId={(row) => row.user_email}
          loading={isLoading}
          onRowClick={onUserClicked}
          {...DATAGRIDPRO_DEFAULTS}
        />
      )}
    </AutoSizer>
  );
};
export default UserTable;
